import React, { useState } from 'react';
import { FileText, Gift } from 'lucide-react';
import {
  useAccountBalance,
  usePurchaseCredits,
  usePaymentHistory,
  useRedeemCoupon,
  useMyCoupons,
  useNgnUsdRate,
} from '@/hooks/useApi';
import { formatCurrency, formatDate, formatNumber, navigation } from '@/lib/utils';
import Button from '@/components/ui/Button';
import LowBalanceSetting from '@/components/billing/LowBalanceSetting';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageSkeleton } from '@/components/ui/Skeleton';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';
import { fieldControl, fieldHint, fieldLabel } from '@/components/ui/fieldStyles';
import { CREDITS_PER_NAIRA, PRODUCT_LABELS, usePriceList } from '@/lib/pricing';

const MIN_NAIRA_PURCHASE = 100; // ₦100 (1,000 credits), the gateway's floor

function getErrorText(err: unknown, fallback: string): string {
  const maybe = err as { response?: { data?: { error?: string } } };
  return maybe?.response?.data?.error ?? fallback;
}

const BillingPage: React.FC = () => {
  const { data: balance, isLoading: balanceLoading } = useAccountBalance();
  const { data: rate } = useNgnUsdRate();
  const { data: prices } = usePriceList();

  const [nairaAmount, setNairaAmount] = useState('');
  const purchaseCredits = usePurchaseCredits();
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const [couponCode, setCouponCode] = useState('');
  const redeemCoupon = useRedeemCoupon();
  const [redeemMessage, setRedeemMessage] = useState<string | null>(null);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const { data: myCoupons } = useMyCoupons();

  const [page, setPage] = useState(1);
  const { data: history, isLoading: historyLoading } = usePaymentHistory(page, 20);

  const parsedAmount = Number(nairaAmount) || 0;
  const usdEstimate = rate && parsedAmount > 0 ? parsedAmount / rate : null;
  const canPurchase = parsedAmount >= MIN_NAIRA_PURCHASE;
  // The gateway sets the credits from the amount: ₦1 = 10 credits.
  const creditsToBuy = Math.floor(parsedAmount * CREDITS_PER_NAIRA);

  const handlePurchase = async () => {
    setPurchaseError(null);
    try {
      const result = await purchaseCredits.mutateAsync({
        amount: Math.round(parsedAmount * 100),
        credits: creditsToBuy,
      });
      navigation.redirectTo(result.payment_url);
    } catch (err) {
      setPurchaseError(getErrorText(err, 'Failed to start purchase. Please try again.'));
    }
  };

  const handleRedeem = async () => {
    setRedeemError(null);
    setRedeemMessage(null);
    try {
      const result = await redeemCoupon.mutateAsync(couponCode.trim());
      setRedeemMessage(result.message);
      setCouponCode('');
    } catch (err) {
      setRedeemError(getErrorText(err, 'Failed to redeem coupon.'));
    }
  };

  if (balanceLoading) {
    return <PageSkeleton tiles={2} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Billing" description="Buy credits, redeem coupons and see your payments." />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Pay as you go">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[13px] font-medium text-muted-foreground">Credit balance</p>
              <p className="mt-2 text-[28px] font-semibold leading-tight tracking-tight tabular-nums text-foreground">{formatNumber(balance?.prepaid_balance ?? 0)} credits</p>
            </div>
            <div>
              <p className="text-[13px] font-medium text-muted-foreground">This month's usage</p>
              <p className="mt-2 text-[28px] font-semibold leading-tight tracking-tight tabular-nums text-foreground">{formatNumber(balance?.current_month_usage ?? 0)} credits</p>
            </div>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Failed calls and sandbox calls cost nothing.</p>
        </Panel>

        <Panel title="Buy credits">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="flex-1">
              <label htmlFor="naira-amount" className={fieldLabel}>Amount (₦)</label>
              <input
                id="naira-amount"
                type="number"
                min={0}
                value={nairaAmount}
                onChange={(e) => setNairaAmount(e.target.value)}
                className={`${fieldControl} font-mono tabular-nums`}
                placeholder="e.g. 5000"
              />
              {usdEstimate !== null && (
                <p className={fieldHint}>≈ {formatCurrency(usdEstimate, 'USD')} USD</p>
              )}
              {parsedAmount > 0 && !canPurchase && (
                <p className="mt-1.5 text-xs text-error">Minimum purchase is ₦{MIN_NAIRA_PURCHASE}.</p>
              )}
            </div>
            <Button className="h-11 sm:mt-[26px]" onClick={handlePurchase} disabled={!canPurchase} loading={purchaseCredits.isLoading}>
              Buy {parsedAmount > 0 ? `${formatNumber(creditsToBuy)} ` : ''}credits
            </Button>
          </div>
          {purchaseError && <p className="mt-3 text-sm text-error" role="alert">{purchaseError}</p>}
          <p className="mt-4 text-xs text-muted-foreground">Payment opens on Paystack. ₦1 buys 10 credits.</p>
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <LowBalanceSetting threshold={balance?.low_balance_threshold} />

        <Panel title="Redeem a coupon">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label htmlFor="coupon-code" className={fieldLabel}>Coupon code</label>
              <input
                id="coupon-code"
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                className={`${fieldControl} font-mono uppercase placeholder:normal-case`}
                placeholder="e.g. WELCOME100"
              />
            </div>
            <Button className="h-11" onClick={handleRedeem} disabled={!couponCode.trim()} loading={redeemCoupon.isLoading} variant="outline" leftIcon={<Gift className="h-4 w-4" />}>
              Redeem
            </Button>
          </div>
          {redeemMessage && <p className="mt-3 text-sm text-success" role="status">{redeemMessage}</p>}
          {redeemError && <p className="mt-3 text-sm text-error" role="alert">{redeemError}</p>}

          {myCoupons && myCoupons.redemptions.length > 0 ? (
            <ul className="mt-4 divide-y divide-border border-t border-border">
              {myCoupons.redemptions.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5 text-sm">
                  <span className="font-mono text-foreground">{r.code}</span>
                  <span className="text-muted-foreground">
                    {r.type === 'percentage' ? `${r.discount_percentage}% off` : `+${formatNumber(r.credits_awarded)} credits`}
                  </span>
                  <span className="font-mono text-xs tabular-nums text-muted-foreground">{formatDate(new Date(r.redeemed_at * 1000))}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">No coupons redeemed yet.</p>
          )}
        </Panel>
      </div>

      {prices && (
        <Panel title="Prices" bodyClassName="px-0 pb-2">
          <p className="max-w-[80ch] px-5 pb-4 text-sm text-muted-foreground">
            One credit is ₦{prices.credit_ngn.toFixed(2)}. Each product has a free monthly allowance, then a price per
            1,000 that falls as your monthly use grows. Prices follow Mapbox's, converted at ₦{formatNumber(prices.usd_ngn)} per
            US$ ({prices.as_of}) and rounded down to the nearest ₦100.
          </p>
          <Table minWidth={620}>
            <THead>
              <tr>
                <Th>Product</Th>
                <Th>Free each month</Th>
                <Th>Then, per 1,000</Th>
              </tr>
            </THead>
            <TBody>
              {prices.products.map((p) => (
                <Tr key={p.id}>
                  <Td className="align-top">
                    <p className="text-foreground">{PRODUCT_LABELS[p.id] ?? p.id}</p>
                    <p className="text-xs text-muted-foreground">Priced as Mapbox {p.mapbox}</p>
                  </Td>
                  <Td mono className="align-top">{formatNumber(p.free_per_month)} {p.unit}s</Td>
                  <Td className="space-y-1 align-top">
                    {p.bands.map((b) => (
                      <p key={b.from} className="font-mono text-[13px] tabular-nums text-foreground">
                        ₦{formatNumber(b.ngn_per_1000)}{' '}
                        <span className="font-sans text-xs text-muted-foreground">
                          ({b.credits_per_unit} credits each, {b.to === null ? `over ${formatNumber(b.from - 1)}` : `${formatNumber(b.from)}–${formatNumber(b.to)}`})
                        </span>
                      </p>
                    ))}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </Panel>
      )}

      <Panel title="Payment history" bodyClassName="px-0 pb-2">
        {historyLoading ? (
          <TableSkeleton rows={3} />
        ) : !history || history.payments.length === 0 ? (
          <EmptyState icon={<FileText />} title="No payment history yet" description="Payments you make for credits show up here." />
        ) : (
          <>
            <Table minWidth={520}>
              <THead>
                <tr>
                  <Th>Date</Th>
                  <Th align="right">Amount</Th>
                  <Th align="right">Credits</Th>
                  <Th>Status</Th>
                </tr>
              </THead>
              <TBody>
                {history.payments.map((p) => (
                  <Tr key={p.invoice_number}>
                    <Td mono>{formatDate(new Date(p.created_at * 1000))}</Td>
                    <Td mono align="right">{formatCurrency(p.amount / 100, 'NGN')}</Td>
                    <Td mono muted align="right">{formatNumber(p.credits_used)}</Td>
                    <Td>
                      <Badge tone={p.status === 'paid' ? 'success' : p.status === 'pending' ? 'warning' : 'error'} className="capitalize">
                        {p.status}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 pt-3">
              <p className="text-sm text-muted-foreground">
                Page {history.pagination.page} of {history.pagination.pages || 1}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= history.pagination.pages}>
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
};

export default BillingPage;
